import React, { useEffect, useRef, useState } from 'react';
import { Terminal as XTerm } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import {
  Card, CardContent, Typography, IconButton, Alert, Button, CircularProgress, Box, Tooltip
} from '@mui/material';
import {
  Terminal as TerminalIcon, Close as CloseIcon, FolderOpen as FolderOpenIcon, SupervisorAccount as RootIcon, PowerOff as PowerOffIcon, Description as LogIcon
} from '@mui/icons-material';
import { FileBrowserWindow } from './FileBrowserWindow';
import { LogViewerWindow } from './LogViewerWindow';
import { useTerminateTerminalSessionMutation } from '@store/api/deviceApi';
import { AUTH_STORAGE_KEYS } from '../config/auth';
import { fontFamilyMono } from '../theme/theme';

interface TerminalProps {
  deviceId: string;
  deviceName?: string;
  onClose?: () => void;
}

export const Terminal: React.FC<TerminalProps> = ({ deviceId, deviceName, onClose }) => {
  const terminalRef = useRef<HTMLDivElement>(null);
  const xtermRef = useRef<XTerm | null>(null);
  const fitAddonRef = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  // Without a token the effect never connects
  const [errorMessage, setErrorMessage] = useState<string>(
    () => (localStorage.getItem(AUTH_STORAGE_KEYS.token) ? '' : 'Authentication token not found')
  );
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>(
    errorMessage ? 'error' : 'connecting'
  );
  // Handed to the side panel once the session is ready
  const [session, setSession] = useState<{ websocket: WebSocket | null; terminal: XTerm } | null>(null);
  const [terminateTerminalSession] = useTerminateTerminalSessionMutation();
  const [sidePanel, setSidePanel] = useState<'files' | 'logs' | null>('files'); // Which side panel to show
  const [splitPosition, setSplitPosition] = useState(70); // Terminal takes 70%, side panel 30%
  const [isDragging, setIsDragging] = useState(false);

  // Input buffering for better performance on high-latency connections
  const inputBufferRef = useRef<Uint8Array[]>([]);
  const flushTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Reconnect state
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const isUnmountedRef = useRef(false);
  const connectWSRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!terminalRef.current) return;
    isUnmountedRef.current = false;

    const fontSize = 12;
    // Initialize XTerm with proper configuration
    const term = new XTerm({
      cursorBlink: true,
      fontSize,
      fontFamily: fontFamilyMono,
      // Bold uses the installed 500 weight (main.tsx loads 400 + 500); 700 would be faux-bolded.
      fontWeightBold: '500',
      theme: {
        foreground: '#f8f8f2',
        background: '#1e1e1e',
        cursor: '#f8f8f2',
        black: '#000000',
        brightBlack: '#555555',
        red: '#ff5555',
        brightRed: '#ff5555',
        green: '#50fa7b',
        brightGreen: '#50fa7b',
        yellow: '#f1fa8c',
        brightYellow: '#f1fa8c',
        blue: '#bd93f9',
        brightBlue: '#bd93f9',
        magenta: '#ff79c6',
        brightMagenta: '#ff79c6',
        cyan: '#8be9fd',
        brightCyan: '#8be9fd',
        white: '#bbbbbb',
        brightWhite: '#ffffff',
      },
      convertEol: false,
      scrollback: 1000,
      allowProposedApi: true,
    });

    // Add addons
    const fitAddon = new FitAddon();
    const webLinksAddon = new WebLinksAddon();

    term.loadAddon(fitAddon);
    term.loadAddon(webLinksAddon);

    // Re-measure container and push the size to the PTY. Using wsRef.current so
    // this keeps working after reconnects.
    const fitAndSendResize = () => {
      const fit = fitAddonRef.current;
      const x = xtermRef.current;
      if (!fit || !x) return;
      fit.fit();
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'resize', cols: x.cols, rows: x.rows }));
      }
    };

    // Open only once both JetBrains Mono weights have loaded: open() measures the cell size, so
    // opening on the fallback font sticks with the wrong cell width.
    // Output written before open() is buffered.
    const font = `${fontSize}px ${fontFamilyMono}`;
    const fontsLoaded = document.fonts
      ? Promise.all([document.fonts.load(font), document.fonts.load(`500 ${font}`)])
      : Promise.resolve();
    fontsLoaded.then(
      () => {
        if (isUnmountedRef.current || !terminalRef.current) return;
        term.open(terminalRef.current);
        fitAndSendResize();
      }
    );

    xtermRef.current = term;
    fitAddonRef.current = fitAddon;

    const token = localStorage.getItem(AUTH_STORAGE_KEYS.token);
    if (!token) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}/api/v1/terminal/ws/terminal/${deviceId}?token=${encodeURIComponent(token)}`;

    // Handle control messages from server
    const handleControlMessage = (message: { type: string; message?: string; session_id?: string }) => {
      switch (message.type) {
        case 'status':
          term.write(`\r\n${message.message}\r\n`);
          break;
        case 'ready':
          setConnectionStatus('connected');
          setSession({ websocket: wsRef.current, terminal: term });
          term.write('\r\n\x1b[32mTerminal connected successfully!\x1b[0m\r\n\r\n');
          break;
        case 'error':
          // Only show terminal-level errors, not SFTP errors (handled by FileBrowserWindow)
          if (!message.message?.includes('SFTP')) {
            setConnectionStatus('error');
            setErrorMessage(message.message || 'Unknown error');
            term.write(`\r\n\x1b[31mError: ${message.message}\x1b[0m\r\n`);
          }
          break;
        case 'pong':
          // Ping response, ignore
          break;
        // SFTP messages - handled by FileBrowserWindow, ignore here
        case 'sftp_list_result':
        case 'sftp_download_start':
        case 'sftp_download_chunk':
        case 'sftp_download_complete':
        case 'sftp_upload_start_ack':
        case 'sftp_upload_chunk_ack':
        case 'sftp_upload_write_progress':
        case 'sftp_upload_result':
        case 'sftp_compress_start':
        case 'sftp_compress_chunk':
        case 'sftp_compress_complete':
        case 'keepalive':
          // Silently ignore - these are handled by FileBrowserWindow
          break;
        default:
          console.log('Unknown control message:', message);
      }
    };

    // Create (or re-create) the WebSocket connection. Called on mount and after
    // each unexpected disconnect to reuse the backend SSH session.
    const connectWS = () => {
      if (isUnmountedRef.current) return;

      // Close any existing socket before replacing it, or it leaks. Detach handlers
      // first so the stale socket's onclose doesn't trigger another reconnect — the
      // manual Reconnect button can fire while a socket is still OPEN/CONNECTING.
      const stale = wsRef.current;
      if (stale && (stale.readyState === WebSocket.OPEN || stale.readyState === WebSocket.CONNECTING)) {
        stale.onopen = stale.onmessage = stale.onerror = stale.onclose = null;
        stale.close(1000);
      }

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('Terminal WebSocket connected');
        reconnectAttemptsRef.current = 0;
        setConnectionStatus('connecting');
        fitAndSendResize();

        // Restart per-connection ping interval
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        if (event.data instanceof Blob) {
          // Binary data (terminal output)
          event.data.arrayBuffer().then(buffer => {
            const data = new Uint8Array(buffer);
            term.write(data);
          });
        } else {
          // Text message (control messages)
          try {
            const message = JSON.parse(event.data);
            handleControlMessage(message);
          } catch (e) {
            // Plain text, write to terminal
            term.write(event.data);
          }
        }
      };

      ws.onerror = () => {
        console.error('Terminal WebSocket error');
      };

      ws.onclose = (event) => {
        console.log('Terminal WebSocket closed:', event.code, event.reason);
        if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }

        if (isUnmountedRef.current) return;

        // Deliberate / non-retryable closes — auto-reconnecting on these just fights an
        // intentional server-side close (auth expiry, policy, server termination):
        //   1000 normal · 1003 unsupported data / device not found ·
        //   1008 policy violation · 1011 server error.
        if (event.code === 1000 || event.code === 1003 || event.code === 1008 || event.code === 1011) {
          const isError = event.code !== 1000;
          setConnectionStatus(isError ? 'error' : 'disconnected');
          if (isError) setErrorMessage(event.reason || 'Connection closed by server');
          return;
        }

        // Unexpected close — auto-reconnect with linear backoff (2s, 4s, 6s… capped at 30s)
        const attempt = reconnectAttemptsRef.current + 1;
        reconnectAttemptsRef.current = attempt;
        const MAX_ATTEMPTS = 10;

        if (attempt <= MAX_ATTEMPTS) {
          const delay = Math.min(2000 * attempt, 30000);
          setConnectionStatus('connecting');
          term.write(`\r\n\x1b[33m[Reconnecting in ${delay / 1000}s… (${attempt}/${MAX_ATTEMPTS})]\x1b[0m\r\n`);
          reconnectTimerRef.current = setTimeout(connectWS, delay);
        } else {
          setConnectionStatus('disconnected');
          setErrorMessage('Connection lost after multiple reconnect attempts');
        }
      };
    };

    connectWSRef.current = connectWS;
    connectWS();

    // Flush buffered input to current WebSocket
    const flushInputBuffer = () => {
      const ws = wsRef.current;
      if (inputBufferRef.current.length === 0 || !ws || ws.readyState !== WebSocket.OPEN) {
        return;
      }

      // Concatenate all buffered data
      const totalLength = inputBufferRef.current.reduce((sum, arr) => sum + arr.length, 0);
      const combined = new Uint8Array(totalLength);
      let offset = 0;
      for (const arr of inputBufferRef.current) {
        combined.set(arr, offset);
        offset += arr.length;
      }

      // Send combined data
      ws.send(combined);
      inputBufferRef.current = [];
    };

    // Handle terminal input with buffering — reads from wsRef so reconnects work
    term.onData((data) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      const encoder = new TextEncoder();
      const encoded = encoder.encode(data);

      // Add to buffer
      inputBufferRef.current.push(encoded);

      // Clear existing timer
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
      }

      // Check if data contains Enter key (\r or \n) or special control characters
      const hasEnter = data.includes('\r') || data.includes('\n');
      const hasControlChar = data.includes('\x03') || data.includes('\x04'); // Ctrl+C, Ctrl+D

      const shouldFlushImmediately = data.length > 10 || hasEnter || hasControlChar;

      if (shouldFlushImmediately) {
        flushInputBuffer();
      } else {
        // Small delay for regular typing (16ms = ~60fps)
        flushTimerRef.current = setTimeout(flushInputBuffer, 16);
      }
    });

    // Handle terminal resize — reads from wsRef so reconnects work
    term.onResize((size) => {
      const ws = wsRef.current;
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'resize',
          cols: size.cols,
          rows: size.rows,
        }));
      }
    });

    window.addEventListener('resize', fitAndSendResize);

    // Also trigger resize when terminal becomes visible.
    const resizeObserver = new ResizeObserver(() => {
      // Don't fit a zero-size/hidden container — fit() can throw or set degenerate dims.
      const el = terminalRef.current;
      if (fitAddonRef.current && el && el.offsetWidth > 0 && el.offsetHeight > 0) {
        fitAddonRef.current.fit();
      }
    });
    if (terminalRef.current) {
      resizeObserver.observe(terminalRef.current);
    }

    // Cleanup
    return () => {
      isUnmountedRef.current = true;

      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);

      // Clear flush timer
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
      }

      // Flush any remaining buffered input before closing
      flushInputBuffer();

      window.removeEventListener('resize', fitAndSendResize);
      resizeObserver.disconnect();

      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000);
      }

      term.dispose();
    };
  }, [deviceId]);

  const handleReconnect = () => {
    // Reconnect without page reload — backend keeps SSH session alive
    reconnectAttemptsRef.current = 0;
    if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    connectWSRef.current?.();
  };

  const handleSwitchToRoot = () => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return;
    }

    // Send switch_to_root command - backend handles password automatically
    ws.send(JSON.stringify({ type: 'switch_to_root' }));
  };

  const handleTerminate = async () => {
    if (!window.confirm('Are you sure you want to terminate the SSH session? This will close all active terminal connections to this device.')) {
      return;
    }

    try {
      await terminateTerminalSession(deviceId).unwrap();

      setConnectionStatus('disconnected');
      setErrorMessage('SSH session terminated successfully');
      // Close with normal-closure code (1000) so onclose treats it as a
      // deliberate close and does NOT auto-reconnect the terminated session.
      if (wsRef.current) {
        wsRef.current.close(1000);
      }
    } catch (error: any) {
      console.error('Error terminating session:', error);
      setErrorMessage(`Failed to terminate session: ${error?.data?.detail || 'Unknown error'}`);
    }
  };

  // Handle splitter drag
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const container = document.getElementById('terminal-container');
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;

      // Constrain between 30% and 80%; ResizeObserver on terminalRef handles the fit.
      if (newPosition >= 30 && newPosition <= 80) {
        setSplitPosition(newPosition);
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Card
      elevation={1}
      sx={{
        height: '600px',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        transition: 'none',
        '&:hover': {
          boxShadow: 'none',
          transform: 'none'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TerminalIcon />
            <Typography variant="h6">Terminal & File Manager</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip title="Switch to root user">
              <IconButton
                size="small"
                onClick={handleSwitchToRoot}
                disabled={connectionStatus !== 'connected'}
                color="warning"
              >
                <RootIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="File Manager">
              <IconButton
                size="small"
                onClick={() => setSidePanel(sidePanel === 'files' ? null : 'files')}
                color={sidePanel === 'files' ? "primary" : "default"}
              >
                <FolderOpenIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Log Viewer">
              <IconButton
                size="small"
                onClick={() => setSidePanel(sidePanel === 'logs' ? null : 'logs')}
                color={sidePanel === 'logs' ? "primary" : "default"}
              >
                <LogIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Terminate SSH session (closes tunnel and all connections)">
              <IconButton
                size="small"
                onClick={handleTerminate}
                disabled={connectionStatus === 'disconnected'}
                color="error"
              >
                <PowerOffIcon />
              </IconButton>
            </Tooltip>
            {onClose && (
              <IconButton
                size="small"
                onClick={onClose}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          {deviceName ? `Connected to ${deviceName}` : `Device: ${deviceId}`}
        </Typography>
      </Box>

      {/* Split pane content */}
      <CardContent id="terminal-container" sx={{ flex: 1, p: 0, display: 'flex', overflow: 'hidden', position: 'relative' }}>
        {/* Terminal pane */}
        <Box sx={{
          width: sidePanel ? `${splitPosition}%` : '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: sidePanel ? 'none' : 'width 0.3s ease-in-out'
        }}>
          {connectionStatus === 'connecting' && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Box sx={{ textAlign: 'center' }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography variant="body2" sx={{
                  color: 'text.secondary'
                }}>Connecting to terminal...</Typography>
              </Box>
            </Box>
          )}

          {connectionStatus === 'error' && (
            <Box sx={{ p: 2 }}>
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage || 'Failed to connect to terminal'}
              </Alert>
              <Button variant="contained" onClick={handleReconnect}>
                Retry Connection
              </Button>
            </Box>
          )}

          {connectionStatus === 'disconnected' && (
            <Box sx={{ p: 2 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Terminal session disconnected
                {errorMessage && `: ${errorMessage}`}
              </Alert>
              <Button variant="contained" onClick={handleReconnect}>
                Reconnect
              </Button>
            </Box>
          )}

          <Box
            ref={terminalRef}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: connectionStatus !== 'connected' && connectionStatus !== 'connecting' ? 'none' : 'block',
              backgroundColor: '#1e1e1e',
              overflow: 'hidden',
              '& .xterm': {
                padding: '4px',
                height: '100%',
                width: '100%'
              },
              '& .xterm-viewport': {
                backgroundColor: '#1e1e1e',
                width: '100% !important',
                overflow: 'auto !important'
              },
              '& .xterm-screen': {
                width: '100% !important'
              }
            }}
          />
        </Box>

        {/* Resizable splitter */}
        {sidePanel && connectionStatus === 'connected' && (
          <Box
            onMouseDown={handleMouseDown}
            sx={{
              width: '4px',
              cursor: 'col-resize',
              backgroundColor: isDragging ? 'primary.main' : 'divider',
              position: 'relative',
              zIndex: 10
            }}
          />
        )}

        {/* Side panel - File browser or Log viewer */}
        {sidePanel && connectionStatus === 'connected' && session && (
          <Box sx={{
            width: `${100 - splitPosition}%`,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {sidePanel === 'files' && (
              <FileBrowserWindow
                deviceId={deviceId}
                deviceName={deviceName}
                websocket={session.websocket}
                terminal={session.terminal}
                inline={true}
              />
            )}
            {sidePanel === 'logs' && (
              <LogViewerWindow
                websocket={session.websocket}
                inline={true}
              />
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
